// ============================================
// NATURALS BLESSING — GLOBAL JAVASCRIPT
// Core functionality for the theme
// ============================================

// ============================================
// MOBILE NAVIGATION
// ============================================
class MobileNavigation {
  constructor() {
    this.toggle = document.getElementById('mobile-menu-toggle');
    this.nav = document.getElementById('mobile-nav');
    this.overlay = document.getElementById('mobile-nav-overlay');
    this.close = document.getElementById('mobile-nav-close');

    if (this.toggle) {
      this.toggle.addEventListener('click', () => this.open());
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeNav());
    }
    if (this.close) {
      this.close.addEventListener('click', () => this.closeNav());
    }
  }

  open() {
    this.nav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeNav() {
    this.nav.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

// ============================================
// ACCORDION
// ============================================
class Accordion {
  constructor(el) {
    this.el = el;
    this.trigger = el.querySelector('.accordion-trigger');
    this.content = el.querySelector('.accordion-content');

    if (this.trigger) {
      this.trigger.addEventListener('click', () => this.toggle());
    }
  }

  toggle() {
    const isOpen = this.el.classList.contains('is-open');
    // Close all other accordions in the same container
    const parent = this.el.closest('.accordion');
    if (parent) {
      parent.querySelectorAll('.accordion-item.is-open').forEach(item => {
        if (item !== this.el) item.classList.remove('is-open');
      });
    }
    this.el.classList.toggle('is-open', !isOpen);
  }
}

// ============================================
// PRODUCT GALLERY
// ============================================
class ProductGallery {
  constructor() {
    this.mainImage = document.getElementById('product-main-image');
    this.thumbs = document.querySelectorAll('.product-gallery__thumb');

    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => this.switchImage(thumb));
    });
  }

  switchImage(thumb) {
    const src = thumb.dataset.src;
    const alt = thumb.dataset.alt || '';

    if (this.mainImage && src) {
      this.mainImage.src = src;
      this.mainImage.alt = alt;
    }

    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }
}

// ============================================
// STICKY ADD TO CART
// ============================================
class StickyATC {
  constructor() {
    this.stickyBar = document.getElementById('sticky-atc');
    this.addToCartSection = document.getElementById('product-atc-section');

    if (!this.stickyBar || !this.addToCartSection) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            this.stickyBar.style.display = 'block';
          } else {
            this.stickyBar.style.display = 'none';
          }
        });
      },
      { threshold: 0 }
    );

    this.observer.observe(this.addToCartSection);
  }
}

// ============================================
// CART AJAX
// ============================================
class CartManager {
  static async addItem(variantId, quantity = 1) {
    // OPTIMISTIC: open drawer immediately before POST completes
    (function nbOpen(){if(typeof openCartDrawerNK==='function'){openCartDrawerNK();}else{setTimeout(nbOpen,50);}})();

    try {
      const response = await fetch(window.routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: variantId, quantity })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const data = await response.json();
      CartManager.updateCartCount();
      // Refresh drawer with confirmed cart data
      fetch('/cart.js').then(r => r.json()).then(cart => {
        if (typeof renderCartDrawerItems === 'function') renderCartDrawerItems(cart);
        if (window._cachedCart !== undefined) window._cachedCart = cart;
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    } catch (error) {
      console.error('Cart error:', error);
    }
  }

  static async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const countEl = document.querySelector('.cart-count-bubble');
      if (countEl) {
        countEl.textContent = cart.item_count;
        countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }
    } catch (error) {
      console.error('Cart count error:', error);
    }
  }

  static showNotification(productTitle) {
    // Disabled — cart drawer handles confirmation instead of toast
  }
}

// ============================================
// ADD TO CART BUTTONS
// ============================================
function initAddToCartButtons() {
  document.querySelectorAll('[data-add-to-cart]').forEach(btn => {
    // Pre-warm cart cache on hover/touch — so drawer opens with data already ready
    const prefetch = () => { if (typeof prefetchCartData === 'function') prefetchCartData(); };
    btn.addEventListener('mouseover', prefetch, { passive: true });
    btn.addEventListener('touchstart', prefetch, { passive: true });

    btn.addEventListener('click', async (e) => {
      if (btn.id === 'add-to-cart-btn') return; // Handled by form submit in theme.js — prevents double drawer open
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      // Optimistic: show success immediately, disable button
      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = '✓ Added!';

      // CartManager.addItem now opens drawer first, then POSTs
      await CartManager.addItem(variantId);

      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    });
  });
}

// ============================================
// COUNTDOWN TIMER (for urgency)
// ============================================
class CountdownTimer {
  constructor(el) {
    this.el = el;
    const endTime = el.dataset.endTime;
    if (!endTime) return;

    this.endDate = new Date(endTime);
    this.update();
    this.interval = setInterval(() => this.update(), 1000);
  }

  update() {
    const now = new Date();
    const diff = this.endDate - now;

    if (diff <= 0) {
      clearInterval(this.interval);
      this.el.style.display = 'none';
      return;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    const hoursEl = this.el.querySelector('[data-hours]');
    const minutesEl = this.el.querySelector('[data-minutes]');
    const secondsEl = this.el.querySelector('[data-seconds]');

    if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
    if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
    if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
  }
}

// ============================================
// SCROLL ANIMATIONS
// ============================================
function initScrollAnimations() {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
  );

  document.querySelectorAll('[data-animate]').forEach(el => {
    observer.observe(el);
  });
}

// ============================================
// ANNOUNCEMENT BAR SLIDER
// ============================================
class AnnouncementBar {
  constructor() {
    this.bar = document.getElementById('announcement-bar');
    if (!this.bar) return;

    this.messages = this.bar.querySelectorAll('.announcement-bar__message');
    if (this.messages.length <= 1) return;

    this.currentIndex = 0;
    this.interval = setInterval(() => this.next(), 4000);
  }

  next() {
    this.messages[this.currentIndex].style.display = 'none';
    this.currentIndex = (this.currentIndex + 1) % this.messages.length;
    this.messages[this.currentIndex].style.display = 'block';
  }
}

// ============================================
// INITIALIZE ALL COMPONENTS
// ============================================
document.addEventListener('DOMContentLoaded', () => {
  new MobileNavigation();
  new ProductGallery();
  new StickyATC();
  new AnnouncementBar();

  // Initialize accordions
  document.querySelectorAll('.accordion-item').forEach(el => new Accordion(el));

  // Initialize countdown timers
  document.querySelectorAll('[data-countdown]').forEach(el => new CountdownTimer(el));

  // Initialize add to cart buttons
  initAddToCartButtons();

  // Initialize scroll animations
  initScrollAnimations();

  // Update cart count on load
  CartManager.updateCartCount();
});


// ============================================
// NB CART DRAWER DEBOUNCE — prevents double-open
// Wraps openCartDrawerNK with a 2-second lock so
// standard-actions.js (Shopify CDN) cannot reopen
// the drawer after the user has closed it.
// ============================================
(function patchCartDrawer() {
  function applyDebounce() {
    if (typeof window.openCartDrawerNK !== 'function') {
      setTimeout(applyDebounce, 50);
      return;
    }
    var _original = window.openCartDrawerNK;
    var _lastOpened = 0;
    window.openCartDrawerNK = function() {
      var now = Date.now();
      // If drawer was opened less than 2000ms ago, ignore this call
      if (now - _lastOpened < 8000) {
        return;
      }
      _lastOpened = now;
      _original.apply(this, arguments);
    };
    // Expose a reset so our own code can force-open after user intentionally clicks
    window._resetCartDrawerLock = function() { _lastOpened = 0; };
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyDebounce);
  } else {
    applyDebounce();
  }
})();
